<template>
  <div class="talent-selftest-box bgcolor">
    <div class="talent-selftest-container">
      <!-- 返回首页按钮 -->
      <!-- <div class="home-link-wrapper">
        <router-link to="/" class="home-link">
          <span class="home-icon">←</span>
          <span class="home-text">返回首页</span>
        </router-link>
      </div> -->
      
      <!-- 调试区域 - 仅开发环境显示 -->
      <div v-if="isDevelopment" class="debug-panel">
        <div class="debug-header">🛠️ 调试工具</div>
        <div class="debug-controls">
          <div class="debug-section">
            <span class="debug-label">快速选择：</span>
            <button 
              v-for="num in [1, 2, 3, 4, 5]" 
              :key="num"
              type="button"
              class="debug-btn"
              @click="fillAllAnswers(num)"
            >
              全选 {{ num }}
            </button>
          </div>
          <div class="debug-section">
            <button 
              type="button"
              class="debug-btn debug-btn-primary"
              @click="fillRandomAnswers"
            >
              🎲 随机填充所有选项
            </button>
            <button 
              type="button"
              class="debug-btn debug-btn-danger"
              @click="clearAllAnswers"
            >
              🗑️ 清空所有答案
            </button>
          </div>
          <div class="debug-section">
            <button 
              type="button"
              class="debug-btn debug-btn-success"
              @click="submitDirectly"
              :disabled="Object.keys(answers).length !== questions.length"
            >
              ⚡ 直接提交查看结果
            </button>
          </div>
        </div>
      </div>
      
      <h1 class="talent-title fontWeight">天赋自测</h1>
      <p class="provenance">
        本测试来自北京联合出版公司出版社的《做自己擅长的事，还是喜欢的？》，作者 [奥] Aljoscha Neubauer。
      </p>
      <div class="talent-intro">
        说明：总共{{ questions.length }}题，每题5个选项。
      </div>
      
      <form class="max-w-sm mx-auto xs:px-6 sm:px-8 mt-4" @submit.prevent="handleSubmit">
        <!-- 进度条 -->
        <div class="progress">
          <span style="margin-right: 0.5rem; font-size: 14px;">{{ currentQuestionIndex + 1 }} / {{ questions.length }}</span>
          <el-progress 
            :percentage="((currentQuestionIndex + 1) / questions.length) * 100"
            :show-text="false"
            :stroke-width="12"
            color="#8e53c4"
            style="width: 80%; display: inline-block;"
          />
        </div>
        
        <!-- 题目容器 -->
        <div class="question-container">
          <p class="question-title fontWeight">
            {{ questions[currentQuestionIndex].question }}
          </p>
          
          <!-- 选项 -->
          <div class="question-options">
            <label 
              v-for="option in optionLabels" 
              :key="`q${currentQuestionIndex}_${option[0]}`"
              class="option"
            >
              <input
                type="radio"
                :name="`question${currentQuestionIndex}`"
                :id="`question_${currentQuestionIndex}_option_${option[0]}`"
                :value="option[0]"
                :checked="String(answers[currentQuestionIndex]) === option[0]"
                @change="handleAnswerChange"
                class="hidden"
              />
              <div 
                class="option-content"
                :class="{ 
                  active: String(answers[currentQuestionIndex]) === option[0] && answers[currentQuestionIndex] !== undefined
                }"
              >
                {{ option[1] }}
              </div>
            </label>
          </div>
        </div>
        
        <!-- 按钮 -->
        <div class="button-container" style="text-align: center;">
          <button
            type="button"
            class="btn btn-fixed"
            :class="{ 
              'opacity-50 cursor-not-allowed': currentQuestionIndex === 0
            }"
            :disabled="currentQuestionIndex === 0"
            @click="handlePreviousQuestion"
          >
            <span>上一题</span>
          </button>
          <button 
            type="submit" 
            class="btn btn-fixed"
            :class="{ 
              'opacity-50 cursor-not-allowed': !answers[currentQuestionIndex],
              'loading': isLoading
            }"
            :disabled="!answers[currentQuestionIndex]"
          >
            <div v-if="isLoading" class="btn-loading">
              <div class="btn-spinner"></div>
              <span></span>
            </div>
            <span v-else>{{ currentQuestionIndex < questions.length - 1 ? '下一题' :
             !answers[currentQuestionIndex] ? '选择后查看' : '查看结果' }}</span>
          </button>
        </div>
      </form>
    </div>
    
    <!-- 恢复进度提示 - 固定定位，不影响布局 -->
    <transition name="fade">
      <div v-if="showRestoreTip" class="restore-tip">
        <span class="restore-icon">✓</span>
        <span class="restore-text">已恢复至第 {{ restoreIndex + 1 }} 题</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { saveProgress, loadProgress, clearProgress } from '@/utils/storage'

export default {
  name: 'TalentTest',
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    }
  },
  data() {
    return {
      isLoading: false,
      autoJumpTimer: null,
      showRestoreTip: false, // 显示恢复提示
      optionLabels: [
        ['5', '非常符合'],
        ['4', '很符合'],
        ['3', '部分符合'],
        ['2', '不太符合'],
        ['1', '完全不符合']
      ],
      questions: [
      { id: 0, question: "在做事情时，我更喜欢加入艺术性的表达，让它更独特和富有美感", category: "J"},
      { id: 1, question: "向别人讲解或传授知识时，我感觉自己完成得很轻松", category: "E" },
      { id: 2, question: "对于需要手眼配合的任务（如打球、穿针线），我通常完成得很好", category: "F" },
      { id: 3, question: "我能流畅而准确地完成一套连贯的动作（如舞蹈动作、体操）", category: "F" },
      { id: 4, question: "听音乐时，我能轻松地跟上节拍", category: "G" },
      { id: 5, question: "通过观察，我总能比较准确地判断出小动物的需求", category: "H" },
      { id: 6, question: "我擅长通过刻画和塑造来制作手工作品或艺术品", category: "J" },
      { id: 7, question: "需要手指精细操作的任务（如手工、组装小零件）是我的强项", category: "F" },
      { id: 8, question: "当需要向别人解释复杂的事情时，我总能说得非常清楚明白", category: "A" },
      { id: 9, question: "在搭配颜色时，我总能让它们看起来和谐又舒服", category: "J" },
      { id: 10, question: "面对数学或物理公式时，我觉得理解它们并不困难", category: "B" },
      { id: 11, question: "学习和完成与身体运动相关的任务，让我感觉轻松自如", category: "F" },
      { id: 12, question: "在集体讨论中，我常常能冒出许多不同的点子", category: "I" },
      { id: 13, question: "在我脑海中，能够轻松地从各个角度想象一个物体的形态", category: "C" },
      { id: 14, question: "遇到难题时，我常常能想出一些与众不同、打破常规的解决方法", category: "I" },
      { id: 15, question: "在分析问题时，我倾向于进行有步骤、有逻辑的思考", category: "B" },
      { id: 16, question: "解决数学问题时，我感到得心应手", category: "B" },
      { id: 17, question: "在辩论或说服别人时，我自信能用自己的语言打动对方", category: "A" },
      { id: 18, question: "我能清晰地意识到自己的情绪变化，并理解为什么会有这样的情绪", category: "D" },
      { id: 19, question: "我喜欢并且擅长用数学计算或模型来论证一个观点", category: "B" },
      { id: 20, question: "我能很好地判断方向和位置，在陌生地方也不容易迷路", category: "C" },
      { id: 21, question: "我善于运用语言来清晰地表达自己的想法和感受", category: "A" },
      { id: 22, question: "我清楚地知道自己的愿望和需求，并且能够坦然地向他人表达", category: "D" },
      { id: 23, question: "在与人相处时，我总能设身处地地为对方着想", category: "E" },
      { id: 24, question: "我的思维方式常常不受固有框架的限制，喜欢天马行空", category: "I" },
      { id: 25, question: "根据地图或指示，我能在脑中清晰地规划出路线和空间关系", category: "C" },
      { id: 26, question: "在面对压力时，我有一套有效的方法能让自己快速平静下来", category: "D" },
      { id: 27, question: "根据文字描述（比如说明书），我能在脑中清晰地构建出相应的画面", category: "C" },
      { id: 28, question: "我享受并且擅长与各种不同的人打交道", category: "E" },
      { id: 29, question: "即使是面对不熟悉的动物，我也能从容地与它相处", category: "H" },
      { id: 30, question: "我的耳朵对声音很敏感，能听出旋律中细微的不和谐之处", category: "G" },
      { id: 31, question: "我善于照料各种不同的植物，懂得它们的需求", category: "H" },
      { id: 32, question: "我喜欢唱歌，并且大家觉得我唱得不错", category: "G" },
      { id: 33, question: "在时间紧迫的情况下处理问题，我能保持冷静并高效应对", category: "D" },
      { id: 34, question: "我能识别多种草药或植物，并了解如何在生活中使用它们", category: "H" },
      { id: 35, question: "我常常能进行原创性的思考，提出新颖的见解", category: "I" },
      { id: 36, question: "我乐于装饰周围的空间，让它变得更具美感", category: "J" },
      { id: 37, question: "与人交流时，我总能迅速理解对方想表达的核心意思", category: "A" },
      { id: 38, question: "学习演奏不同的乐器，对我来说是一件有趣且不太难的事", category: "G" },
      { id: 39, question: "当朋友之间发生矛盾时，我常常能成功地从中调解", category: "E" }
      ],
      talent: {
        A: "语言天赋",
        B: "逻辑-算数天赋",
        C: "空间天赋",
        D: "内省天赋",
        E: "人际天赋",
        F: "身体-动觉天赋",
        G: "音乐天赋",
        H: "自然天赋",
        I: "创造天赋",
        J: "美学天赋"
      },
      currentQuestionIndex: 0,
      restoreIndex: 0,
      answers: {},
      scores: {}
    }
  },
  methods: {
    handleSubmit(event) {
      event.preventDefault()

      // 打印选择的答案
      console.log("选择的答案:", this.answers)
      console.log("当前题目索引:", this.currentQuestionIndex)
      console.log("题目数量:", this.questions.length)
      console.log("是否是最后一题:", this.currentQuestionIndex == this.questions.length - 1)
      console.log("当前题目答案:", this.answers[this.currentQuestionIndex])
      console.log("当前题目答案是否大于0:", this.answers[this.currentQuestionIndex] > 0)
      
      if (this.currentQuestionIndex < this.questions.length - 1) {
        // 下一题
        if (this.answers[this.currentQuestionIndex] && 
            this.answers[this.currentQuestionIndex] > 0) {
          this.currentQuestionIndex = this.currentQuestionIndex + 1
        } else {
          // 提示需要选择后才能下一题
          this.$message.error('请选择答案后跳转下一题~')
        }
      } else {
        // 校验是否所有都选择过了
        if (Object.keys(this.answers).length !== this.questions.length) {
          this.$message.error('请完成所有题目，当前已完成' + Object.keys(this.answers).length + '题')
          return
        }

        // 查看结果
        if (this.answers[this.currentQuestionIndex] && 
            this.answers[this.currentQuestionIndex] > 0) {
          this.calculateScores()
          this.$store.dispatch('saveTestResults', this.scores)
          this.$router.push('/result')
        } else {
          // 提示答案错误，需要重新开始
          this.$message.error('请选择答案后再查看结果哦~')
        }
      }
    },
    
    handleAnswerChange(event) {
      console.log("当前题目:", this.currentQuestionIndex, "选择答案:", event.target.value)
      
      this.answers[this.currentQuestionIndex] = parseInt(event.target.value)
      
      // 移除焦点和活动状态，解决 iOS Safari 的粘性 hover 问题
      if (event.target) {
        event.target.blur()
      }
      // 同时移除父元素的焦点
      const parentLabel = event.target.closest('label')
      if (parentLabel) {
        parentLabel.blur()
      }
      
      // 保存进度（保存当前答案和当前索引）
      // 注意：只在选择答案时保存，跳转后不需要再次保存
      // 因为如果用户刷新，应该恢复到当前题（刚选择答案的题），而不是已经跳转的下一题
      this.saveProgressNow()
      
      // 自动跳转到下一题（带loading效果）
      // 注意：在最后一题时不自动跳转，让用户手动点击"查看结果"按钮
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.isLoading = true
        
        // 保存当前题目索引，防止在延迟期间用户再次点击导致索引错乱
        const targetIndex = this.currentQuestionIndex + 1
        
        this.autoJumpTimer = setTimeout(() => {
          console.log("跳转后索引：", targetIndex, "题目数量：", this.questions.length)
          // 再次检查，确保没有超出范围
          if (targetIndex < this.questions.length) {
            this.currentQuestionIndex = targetIndex
            // 跳转后保存一次进度（保存新的索引状态）
            // 这样如果用户在跳转后刷新，能恢复到正确的题目
            this.saveProgressNow()
          } else {
            console.log("索引不一致，跳转后索引：", targetIndex, "题目数量：", this.questions.length)
          }
          this.isLoading = false
        }, 300)
      }
    },
    
    handlePreviousQuestion() {
      // 清除自动跳转的定时器
      if (this.autoJumpTimer) {
        clearTimeout(this.autoJumpTimer)
        this.autoJumpTimer = null
        this.isLoading = false
      }
      
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex = this.currentQuestionIndex - 1
        // 切换题目后保存进度
        this.saveProgressNow()
      } else {
        this.currentQuestionIndex = 0
      }
    },
    
    calculateScores() {
      this.scores = {}
      // 使用 questions 数组的索引来确保顺序正确
      // answers 对象的键是题目索引（字符串），需要转换为数字
      this.questions.forEach((question, index) => {
        const answer = this.answers[index]
        if (answer && answer > 0) {
          const category = question.category
          this.scores[category] = (this.scores[category] || 0) + parseInt(answer)
        }
      })
      // 测试完成后清除进度
      clearProgress()
    },
    
    /**
     * 保存进度
     */
    saveProgressNow() {
      console.log('保存进度', {
        answers: this.answers,
        currentQuestionIndex: this.currentQuestionIndex,
        questionsCount: this.questions.length
      })
      
      saveProgress({
        answers: this.answers,
        currentQuestionIndex: this.currentQuestionIndex,
        questionsCount: this.questions.length
      })
    },
    
    /**
     * 恢复进度
     */
    restoreProgress() {
      const progress = loadProgress()
      if (progress) {
        // 验证题目数量是否匹配
        if (progress.questionsCount === this.questions.length) {
          this.answers = { ...progress.answers }
          this.currentQuestionIndex = progress.currentQuestionIndex
          this.restoreIndex = progress.currentQuestionIndex
          
          // 显示恢复提示
          this.showRestoreTip = true
          setTimeout(() => {
            this.showRestoreTip = false
          }, 3000) // 3秒后自动隐藏
          
          return true
        } else {
          // 题目数量不匹配，清除旧数据
          clearProgress()
        }
      }
      return false
    },
    
    // 调试功能方法
    fillAllAnswers(value) {
      // 为所有题目填充相同的答案
      const newAnswers = {}
      this.questions.forEach((_, index) => {
        newAnswers[index] = value
      })
      this.answers = newAnswers
    },
    
    fillRandomAnswers() {
      // 为所有题目随机填充答案（1-5之间）
      const newAnswers = {}
      this.questions.forEach((_, index) => {
        newAnswers[index] = Math.floor(Math.random() * 5) + 1
      })
      this.answers = newAnswers
    },
    
    clearAllAnswers() {
      // 清空所有答案
      this.answers = {}
      this.currentQuestionIndex = 0
      // 清除存储的进度
      clearProgress()
    },
    
    submitDirectly() {
      // 直接提交并查看结果
      if (Object.keys(this.answers).length === this.questions.length) {
        this.calculateScores()
        this.$store.dispatch('saveTestResults', this.scores)
        this.$router.push('/result')
      }
    }
  },
  
  mounted() {
    // 组件挂载时尝试恢复进度
    this.restoreProgress()
  },
  
  beforeUnmount() {
    // 组件销毁前清除定时器
    if (this.autoJumpTimer) {
      clearTimeout(this.autoJumpTimer)
      this.autoJumpTimer = null
    }
    // 组件销毁前保存一次进度
    this.saveProgressNow()
  }
}
</script>

<style scoped>
.talent-selftest-box {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 3rem 0.8rem;
}

.talent-selftest-container {
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 10px;
  padding: 30px 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* 返回首页按钮样式 */
.home-link-wrapper {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
}

.home-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(142, 83, 196, 0.1);
  color: #8e53c4;
  text-decoration: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.home-link:hover {
  background: rgba(142, 83, 196, 0.2);
  transform: translateX(-3px);
}

.home-icon {
  font-size: 18px;
  font-weight: bold;
}

.home-text {
  font-size: 14px;
}

/* 调试面板样式 */
.debug-panel {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.debug-header {
  color: white;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 12px;
  text-align: center;
}

.debug-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.debug-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.debug-label {
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-right: 5px;
}

.debug-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 6px 12px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.debug-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

.debug-btn:active:not(:disabled) {
  transform: translateY(0);
}

.debug-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.debug-btn-primary {
  background: rgba(76, 175, 80, 0.3);
  border-color: rgba(76, 175, 80, 0.5);
}

.debug-btn-primary:hover:not(:disabled) {
  background: rgba(76, 175, 80, 0.5);
}

.debug-btn-danger {
  background: rgba(244, 67, 54, 0.3);
  border-color: rgba(244, 67, 54, 0.5);
}

.debug-btn-danger:hover:not(:disabled) {
  background: rgba(244, 67, 54, 0.5);
}

.debug-btn-success {
  background: rgba(255, 193, 7, 0.3);
  border-color: rgba(255, 193, 7, 0.5);
  font-weight: 600;
}

.debug-btn-success:hover:not(:disabled) {
  background: rgba(255, 193, 7, 0.5);
}

.talent-title {
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.75em;
  font-weight: 700;
}

.provenance {
  margin-bottom: 1rem;
  color: #ccc;
  font-size: 12px;
  text-align: justify;
}

.talent-intro {
  margin: 0;
  color: #333;
  font-size: 14px;
}

.progress {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #333;
}

.question-container {
  margin-bottom: 2rem;
}

.question-title {
  margin-bottom: 0.75rem;
  color: #333;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
}

.question-options {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.option {
  cursor: pointer;
  transition: all 0.2s ease;
  /* 移除 iOS Safari 的触摸高亮效果 */
  -webkit-tap-highlight-color: transparent;
}

.option input.hidden {
  display: none;
}

.option-content {
  border: 1px solid #dedede;
  border-radius: 5px;
  padding: 0 10px;
  height: 2.2rem;
  line-height: 2.2rem;
  width: 100%;
  text-align: left;
  transition: all 0.3s ease;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  /* 移除 iOS Safari 的触摸高亮效果 */
  -webkit-tap-highlight-color: transparent;
  /* 防止触摸时的用户选择行为 */
  -webkit-user-select: none;
  user-select: none;
}

/* 只在真正支持 hover 的设备上应用悬停效果，避免 iOS 触摸设备的粘性 hover 问题 */
@media (hover: hover) and (pointer: fine) {
  .option-content:hover {
    border-color: #8e53c4;
    background: #f8f4ff;
  }
}

.option-content.active {
  border-color: #8e53c4;
  background: rgba(142, 83, 196, 0.15);
  color: #8e53c4;
}


.btn {
  background: #8e53c4;
  color: white;
  border: none;
  padding: .5rem 2.5rem;
  border-radius: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 120px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.button-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.btn-fixed {
  min-width: 140px;
  width: 140px;
  height: 40px;
  flex-shrink: 0;
  vertical-align: top;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  background: #7a47b0;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.loading {
  pointer-events: none;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.opacity-50 {
  opacity: 0.5;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.max-w-sm {
  max-width: 24rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.mt-4 {
  margin-top: 1rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.text-white {
  color: white;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.rounded {
  border-radius: 0.375rem;
}

.mt-8 {
  margin-top: 2rem;
}


@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 恢复提示样式 - 固定定位，悬浮在顶部，贴合页面内容样式 */
.restore-tip {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: white;
  color: #8e53c4;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 15px rgba(142, 83, 196, 0.2);
  border: 1px solid rgba(142, 83, 196, 0.2);
  min-width: 200px;
  max-width: 90%;
  animation: slideDown 0.3s ease-out;
}

.restore-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: rgba(142, 83, 196, 0.1);
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  color: #8e53c4;
}

.restore-text {
  flex: 1;
  text-align: center;
  color: #8e53c4;
}

/* 淡入淡出动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@media (max-width: 640px) {
  .talent-selftest-container {
    padding: 30px 20px;
    margin: 0 0.5rem;
  }
  
  .restore-tip {
    padding: 8px 16px;
    font-size: 13px;
    top: 15px;
    min-width: 180px;
    border-radius: 18px;
  }
  
  .restore-icon {
    width: 16px;
    height: 16px;
    font-size: 11px;
  }
}
</style>
